import { useMemo, useState } from 'react';
import { screenSchedule, type Showtime } from '@/mocks/cinema';

const days = [
  { d: '今天', date: '09.07', w: '周三' },
  { d: '明天', date: '09.08', w: '周四' },
  { d: '周五', date: '09.09', w: '周五' },
  { d: '周六', date: '09.10', w: '周六' },
  { d: '周日', date: '09.11', w: '周日' },
];

function TimeRow({ s }: { s: Showtime }) {
  const soldOut = s.seats === 0;
  return (
    <a
      href="#schedule"
      onClick={(e) => e.preventDefault()}
      className="group flex flex-col items-start gap-1 rounded-md border border-background-200 bg-background-50 px-3 py-2.5 transition-colors hover:border-primary-400 hover:bg-primary-50 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-center gap-2">
        <span className="font-display text-sm font-bold text-foreground-950">{s.time}</span>
        <span className="rounded bg-background-100 px-1.5 py-0.5 text-[10px] text-foreground-500">{s.type}</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-foreground-600">
        <span>{s.hall}</span>
        <span className="text-foreground-400">·</span>
        <span>{s.lang}</span>
        <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] font-medium text-secondary-800">
          ¥{s.price}
        </span>
      </div>
      <span className={`text-xs ${soldOut ? 'text-secondary-500' : 'text-primary-600'}`}>
        {soldOut ? '已售罄' : `余 ${s.seats} 座`}
      </span>
    </a>
  );
}

export default function Schedule() {
  const [day, setDay] = useState(days[0].d);
  const [movieIdx, setMovieIdx] = useState(0);
  const [cinema, setCinema] = useState('城中万达影城');

  const cinemas = ['城中万达影城', '星河 IMAX 旗舰店', '时代影城(滨江店)'];

  const active = useMemo(
    () => screenSchedule[movieIdx] ?? screenSchedule[0],
    [movieIdx],
  );

  const totalSeats = active.times.reduce((a, t) => a + (t.total - t.seats), 0);
  const availSeats = active.times.reduce((a, t) => a + t.seats, 0);
  const seatRate = Math.round((totalSeats / (active.times.length * 200 || 1)) * 100);

  return (
    <section id="schedule" className="bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              <i className="ri-calendar-2-line"></i>
              今日排片
            </span>
            <h2 className="mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">
              影院排片 · 场次实况
            </h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">
              选择日期与影院，快速浏览各部影片在今天的放映时间、影厅、版本与实时余座，热门场次抢先锁定。
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex flex-wrap items-center gap-1 rounded-lg bg-background-200 p-1">
              {cinemas.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCinema(c)}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    cinema === c
                      ? 'bg-background-50 text-primary-700 shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-50 p-2">
          {days.map((d) => (
            <button
              key={d.d}
              type="button"
              onClick={() => setDay(d.d)}
              className={`flex flex-col items-center rounded-md px-4 py-2 transition-colors ${
                day === d.d
                  ? 'bg-primary-600 text-background-50'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <span className="text-xs font-medium">{d.w}</span>
              <span className="text-sm font-bold">{d.d === '今天' || d.d === '明天' ? `${d.d} ${d.date}` : `${d.date}`}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="space-y-3 lg:col-span-1">
            {screenSchedule.map((m, idx) => (
              <button
                key={m.title}
                type="button"
                onClick={() => setMovieIdx(idx)}
                className={`flex w-full items-center gap-3 rounded-lg border bg-background-50 p-3 text-left transition-all ${
                  movieIdx === idx
                    ? 'border-primary-400 ring-1 ring-primary-200'
                    : 'border-background-200 hover:border-background-300'
                }`}
              >
                <img
                  src={m.img}
                  alt={`${m.title} 海报`}
                  className="h-16 w-12 shrink-0 rounded-md object-cover"
                />
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-heading text-sm font-bold text-foreground-950">{m.title}</h3>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    {m.genre} · {m.length}
                  </p>
                  <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-800">
                    <i className="ri-star-fill text-accent-500"></i> {m.rate}
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">{active.title}</h3>
                  <p className="mt-1 text-xs text-foreground-500">
                    {cinema} · {day} · {active.genre} · {active.length} · 观众评分 {active.rate}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-xs text-foreground-600">
                  <span>
                    今日已售 <b className="text-primary-600">{totalSeats}</b> 座
                  </span>
                  <span>
                    余座 <b className="text-accent-700">{availSeats}</b> 座
                  </span>
                  <span>
                    上座率 <b className="text-foreground-900">{seatRate}%</b>
                  </span>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                {active.times.map((t) => (
                  <TimeRow key={t.time} s={t} />
                ))}
              </div>

              <p className="mt-4 rounded-md bg-secondary-50 px-3 py-2 text-xs text-secondary-800">
                <i className="ri-information-line mr-1"></i>
                场次与余座为实时参考，最终以影城当日前台出票为准；已售罄场次建议切换影城或日期。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}